// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { LedgerAccountType } from '@/account/api/types';
import { LEDGER_ACCOUNT_LABELS } from '@/account/constants/ledger-account-labels';
import { CachedLedgerAccountListProperty } from '@/audit/api/types';
import { Stack, Typography } from '@mui/material';

interface Props {
  property: CachedLedgerAccountListProperty;
}

/**
 * Displays ledger account changes in the audit log.
 * Shows which ledger accounts were added, removed, or updated.
 */
export function AuditLedgerAccountListLabel(props: Props) {
  const delta = props.property.newValue;

  if (!delta) {
    return null;
  }

  // Flatten all changes into a single sorted list
  const changes: {
    accountType: LedgerAccountType;
    displayText: string;
    action: string;
  }[] = [];

  // Added accounts
  delta.added?.forEach((item) => {
    const label = LEDGER_ACCOUNT_LABELS[item.accountType];
    changes.push({
      accountType: item.accountType,
      displayText: `${label.label}: ${item.ledgerAccountName}`,
      action: 'added',
    });
  });

  // Removed accounts
  delta.removed?.forEach((item) => {
    const label = LEDGER_ACCOUNT_LABELS[item.accountType];
    changes.push({
      accountType: item.accountType,
      displayText: `${label.label}: ${item.ledgerAccountName}`,
      action: 'removed',
    });
  });

  // Updated accounts
  delta.updated?.forEach((item) => {
    const label = LEDGER_ACCOUNT_LABELS[item.new.accountType];
    changes.push({
      accountType: item.new.accountType,
      displayText: `${label.label}: ${item.old.ledgerAccountName} → ${item.new.ledgerAccountName}`,
      action: 'updated',
    });
  });

  if (changes.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {changes.map(({ accountType, displayText, action }) => (
        <Typography key={accountType} variant="caption" marginLeft={2}>
          <Typography component="span" variant="inherit">
            {displayText}
          </Typography>{' '}
          <Typography
            component="span"
            variant="inherit"
            sx={{ textDecoration: 'underline' }}
          >
            {action}
          </Typography>
        </Typography>
      ))}
    </Stack>
  );
}
